import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { Observable, throwError, of, timer } from 'rxjs';
import { TierOneRequiredDocument, TierOneDocumentType, TierOneRequest, TierOneRequestInput, TierOneStatus, TierOneDocument, TierOneRequiredDocuments, TierOneStatusType } from '../models/tier-one';
import { switchMap, filter, take, timeout, catchError } from 'rxjs/operators';
import { FileUpload } from '../models/file-upload';
import _keyBy from 'lodash-es/keyBy';
import { ProfileService } from './profile.service';
import { FundsSourceType } from '../models/funds-source';
import { VerificationAdditionalDocumentDataInput, VerificationAdditionalDocument } from '../models/verification-additional-document';
import { Country } from '../models/country';

@Injectable({
  providedIn: 'root'
})
export class TierOneVerificationService {
  readonly PULL_STATUS_PERIOD = 0.5 * 60 * 1000;
  readonly PULL_STATUS_TIMEOUT = 5 * 60 * 1000;

  static readonly requestQuery = `
    status
    statusDescription
    analysisErrors{
      identifier
      title
      message
    }
    dateOfLastChange
    nationality{
      id
      name
      code
      code3
      gcc
    }
    secondaryNationality{
      id
      name
      code
      code3
      gcc
    }
    address{
      addressLine1
      city
      area
      country{
        id
        name
        code
        code3
        gcc
      }
      zip
    }
    fundsSourceType{
      id
      name
      isSpecificationNeeded
    }
    otherFundsSource
    termsAndConditionsAgreement
    uploadedDocuments{
      id
      type
      name
      mimeType
      backSide
      data{
        documentNumber
        expirationDate
      }
      isContentUploaded
    }
  `;

  constructor(private api: ApiService, private profileService: ProfileService) { }

  getRequiredDocuments(payload: {country: string, nationality: string}): Observable<TierOneRequiredDocuments> {
    return this.api.get(`{
      t1RequiredDocuments(
        countryId: ${payload.country},
        nationalityId: ${payload.nationality}
      ) {
        name
        type
        isRequired
        isDocumentNumberNeeded
        isExpirationDateNeeded
        isBackSideNeeded
      }
    }`).pipe(
      switchMap(response => {
        if (response['errors']) {
          return throwError(response);
        }

        const data: TierOneRequiredDocument[] = response['data']['t1RequiredDocuments'];
        return of(_keyBy(data, 'type'));
      }),
      this.api.unifyErrors()
    );
  }

  getStatus(): Observable<TierOneStatus> {
    return this.api.get(`{
      t1VerificationRequestStatus {
        request{
          ${TierOneVerificationService.requestQuery}
        }
        canTemporarilySkip
        skipped
        temporarilySkippedUntil
      }
    }`).pipe(
      switchMap(response => {
        if (response['data']['t1VerificationRequestStatus']) {
          return of(response['data']['t1VerificationRequestStatus']);
        }

        return throwError(response);
      }),
      this.api.unifyErrors()
    );
  }

  waitForStatus() {
    return timer(0, this.PULL_STATUS_PERIOD).pipe(
      switchMap(_ => this.getStatus()),
      catchError(_ => this.getStatus()),
      filter(data => !data || !data.request || data.request.status !== TierOneStatusType.IN_PROGRESS),
      take(1),
      timeout(this.PULL_STATUS_TIMEOUT)
    );
  }

  getAdditionalDocuments(): Observable<VerificationAdditionalDocument[]> {
    return this.api.get(`{
      t1DocumentRequests {
        id
        type
        comment
        dateRequested
        isBackSideNeeded
        isExpired
        expiresAt
        isDocumentNumberNeeded
        isExpirationDateNeeded
        areAdditionalDataSubmitted
        isCompleted
        frontSideAttachment {
          name
          mimeType
          backSide
          isContentUploaded
        }
        backSideAttachment {
          name
          mimeType
          backSide
          isContentUploaded
        }
      }
    }`).pipe(
      switchMap(response => {
        if (response['errors']) {
          return throwError(response);
        }

        return of(response['data']['t1DocumentRequests']);
      }),
      this.api.unifyErrors()
    );
  }

  save(payload: TierOneRequestInput): Observable<TierOneRequest> {
    return this.api.get(`mutation createT1VerificationRequest($payload: T1VerificationRequestInput!){
      createT1VerificationRequest(payload: $payload) {
          ...on T1VerificationRequest{
          ${TierOneVerificationService.requestQuery}
        }
        ... on OperationError{
          errors{
            message
            field
          }
        }
      }
    }`, {
      payload: payload
    }).pipe(
      switchMap(response => {
        const data = response['data']['createT1VerificationRequest'];

        if (data['errors']) {
          return throwError(data);
        }

        if (!payload.draft) {
          this.profileService.clearCache();
        }
        return of(data);
      }),
      this.api.unifyErrors()
    );
  }

  uploadDocument(payload: {type: TierOneDocumentType, document: FileUpload, backSide: boolean}): Observable<TierOneDocument> {
    return this.api.get(`mutation{
      uploadT1DocumentFile(
        upload: {
          type: ${payload.type},
          document: {
            name: "${payload.document.name}",
            content: "${payload.document.content}"
          },
          backSide: ${payload.backSide}
        }
      ) {
         ...on T1VerificationAttachment{
          id
          type
          name
          mimeType
          backSide
          data{
            documentNumber
            expirationDate
          }
          isContentUploaded
        }
        ... on OperationError{
          errors{
            message
            field
          }
        }
      }
    }`).pipe(
      switchMap(response => {
        const data = response['data']['uploadT1DocumentFile'];

        if (data['errors']) {
          return throwError(data);
        }

        return of(data);
      }),
      this.api.unifyErrors()
    );
  }

  removeDocument(id: string): Observable<boolean> {
    return this.api.get(`mutation{
      removeT1UploadedDocument(id: ${id}) {
         ...on GeneralResponse{
          success
        }
        ... on OperationError{
          errors{
            message
            field
          }
        }
      }
    }`).pipe(
      switchMap(response => {
        const data = response['data']['removeT1UploadedDocument'];

        if (data['errors']) {
          return throwError(data);
        }

        return of(data['success']);
      }),
      this.api.unifyErrors()
    );
  }

  uploadAdditionalDocument(payload: {id: string, file: FileUpload, backSide: boolean}): Observable<boolean> {
    return this.api.get(`mutation uploadT1DocumentRequestFile($id: Int!, $file: FileUploadInput!, $backSide: Boolean){
      uploadT1DocumentRequestFile(
        id: $id
        file: $file,
        backSide: $backSide
      ) {
         ...on T1DocumentRequestFilePayload{
          success
        }
        ... on OperationError{
          errors{
            message
            field
          }
        }
      }
    }`, payload).pipe(
      switchMap(response => {
        const data = response['data']['uploadT1DocumentRequestFile'];

        if (data['errors']) {
          return throwError(data);
        }

        return of(data['success']);
      }),
      this.api.unifyErrors()
    );
  }

  submitAdditionalDocumentData(id: string, payload: VerificationAdditionalDocumentDataInput): Observable<boolean> {
    return this.api.get(`mutation submitT1DocumentRequestAdditionalData($id: Int!, $payload: T1DocumentRequestAdditionalDataInput!){
      submitT1DocumentRequestAdditionalData(
        id: $id
        payload: $payload,
      ) {
         ...on T1DocumentRequest{
          isCompleted
        }
        ... on OperationError{
          errors{
            message
            field
          }
        }
      }
    }`, {
      id,
      payload
    }).pipe(
      switchMap(response => {
        const data = response['data']['submitT1DocumentRequestAdditionalData'];

        if (data['errors']) {
          return throwError(data);
        }

        return of(data['isCompleted']);
      }),
      this.api.unifyErrors()
    );
  }

  getFundsSources(): Observable<FundsSourceType[]> {
    return this.api.get(`{
      fundsSources {
        id
        name
        isSpecificationNeeded
      }
    }`).pipe(
      switchMap(response => {
        if (response['errors']) {
          return throwError(response);
        }

        return of(response['data']['fundsSources']);
      }),
      this.api.unifyErrors()
    );
  }
}
